import { SeoFooter } from "@/components/MarketingShell";
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import BrandLogo from "@/components/BrandLogo";
import AlbumCoverRing from "@/components/AlbumCoverRing";
import MobileAlbumStrip from "@/components/MobileAlbumStrip";
import { Music2, Globe2, BarChart3, Sparkles, ArrowRight, Check, Headphones, ShieldCheck, Wand2, Bot, Radio, Cpu, Heart, Zap } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1655841439659-0afc60676b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHNvdW5kJTIwd2F2ZSUyMGRhcmt8ZW58MHx8fHwxNzgwNTExOTk1fDA&ixlib=rb-4.1.0&q=85";
const CROWD_IMG = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxsaXZlJTIwY29uY2VydCUyMGNyb3dkJTIwbmlnaHR8ZW58MHx8fHwxNzgwNTExOTk1fDA&ixlib=rb-4.1.0&q=85";

const PLATFORMS = ["Spotify", "Apple Music", "TikTok", "Amazon Music", "YouTube Music", "Deezer", "Tidal", "Pandora"];

const AI_GENERATORS = [
  "Suno AI", "Udio", "Meta MusicGen", "Stable Audio", "ElevenLabs Music",
  "AIVA", "Boomy", "Soundraw", "Mubert", "Loudly", "Riffusion", "Beatoven",
];

const TIERS = [
  { name: "Starter", price: "$0", period: "/yr", desc: "Release your first single, free.", features: ["1 single / year", "All major DSPs", "Standard support", "85% royalties"], cta: "Start Starter", planId: "starter" },
  { name: "Pro", price: "$10", originalPrice: "$20", discountBadge: "50% OFF", period: "/yr", desc: "For AI artists distributing consistently.", features: ["Unlimited releases", "Built-in AI song generator", "Pre-saves & smart links", "100% royalties", "Priority QC"], cta: "Go Pro", featured: true, planId: "pro" },
  { name: "Label", price: "$50", period: "/yr", desc: "Multi-artist AI labels.", features: ["Unlimited artists & releases", "Bulk AI generation", "Advanced analytics API", "Sub-label management", "100% royalties"], cta: "Go Label", planId: "label" },
];

export default function Landing() {
  // Arriving from another page via /#features, /#pricing, /#faq — scroll to that section.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, []);
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const launch = () => navigate(user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/auth?signup=1");

  const goPlan = async (planId) => {
    if (planId === "starter") { launch(); return; }
    if (!user) { navigate(`/auth?signup=1&plan=${planId}`); return; }
    // Logged-in user → redirect to Stripe checkout via Payments page (handles success/cancel)
    navigate(`/payments?plan=${planId}&checkout=1`);
  };
  const isLight = theme === "light";

  return (
    <div className={`landing min-h-screen grain overflow-x-hidden ${isLight ? "landing-light" : ""}`}>
      {/* Futuristic AI grid backdrop */}
      <div className="landing-grid" aria-hidden="true" />
      <div className="landing-orbit" aria-hidden="true" />

      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-50 landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="nav-logo">
            <BrandLogo size={28} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm landing-muted">
            <a href="#features" className="hover:opacity-100 transition-colors" data-testid="nav-features">Features</a>
            <a href="#pricing" className="hover:opacity-100 transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#faq" className="hover:opacity-100 transition-colors" data-testid="nav-faq">FAQ</a>
            <Link to="/blog" className="hover:opacity-100 transition-colors" data-testid="nav-blog">Blog</Link>
            <Link to="/contact" className="hover:opacity-100 transition-colors" data-testid="nav-contact">Contact</Link>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <ThemeToggle />
            {!user && <Link to="/auth" className="hidden sm:inline text-sm landing-fg hover:opacity-80" data-testid="nav-login">Sign in</Link>}
            <Button onClick={launch} className="bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full px-3 sm:px-5 h-9 sm:h-10 text-xs sm:text-sm" data-testid="nav-cta-launch">
              {user ? "Launch Dashboard" : "Get Started"} <ArrowRight size={14} className="ml-1 hidden sm:inline" />
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-16 sm:pt-20 lg:pt-20 pb-16 sm:pb-24 lg:pb-28 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 xl:gap-24 items-center">
          <div className="lg:col-span-7 relative z-10 min-w-0">
            {/* Mobile-only scrolling album strip — placed BEFORE the hero
                tagline so phones feel alive the moment the page paints. */}
            <MobileAlbumStrip />
            <div className="inline-flex items-center gap-2 text-xs landing-muted border landing-border rounded-full px-3 py-1 mb-5 backdrop-blur">
              <span className="text-[#FF4500] text-sm leading-none" aria-hidden="true">✦</span> The first AI generated music distribution platform
            </div>
            <h1 className="font-display text-[2.1rem] leading-[1.08] sm:text-6xl lg:text-7xl sm:leading-[1.05] font-semibold tracking-tight break-words">
              Generate. Release.{" "}
              <span className="gradient-text-accent">Distribute AI music globally.</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg landing-muted max-w-2xl leading-relaxed">
              The world's first music distributor <em className="not-italic landing-fg font-medium">exclusively focused</em> on<br className="hidden md:inline" />{" "}
              AI-generated music — bring your tracks from <strong className="landing-fg">Suno, Udio</strong> or<br className="hidden md:inline" />{" "}
              our own AI Studio, and we distribute them to Spotify, Apple Music,<br className="hidden md:inline" />{" "}
              TikTok, Facebook and every major DSP with AI-disclosure.
            </p>
            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
              <Button onClick={launch} className="bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full h-12 px-7 dm-accent-glow w-full sm:w-auto justify-center" data-testid="hero-cta-launch">
                {user ? "Launch Dashboard" : "Launch Dashboard"} <ArrowRight size={16} className="ml-2" />
              </Button>
              <a href="#ai-studio" className="text-sm landing-fg hover:opacity-80 text-center sm:text-left py-2 sm:py-0" data-testid="hero-cta-ai">See AI Studio →</a>
            </div>
            <div className="mt-8 sm:mt-10 grid grid-cols-3 sm:flex sm:flex-wrap sm:items-center gap-x-4 sm:gap-x-8 gap-y-3 text-[11px] sm:text-sm landing-muted">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-2 text-center sm:text-left"><ShieldCheck size={14} className="text-[#FF4500] shrink-0" /> <span>Auto AI-disclosure</span></div>
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-2 text-center sm:text-left"><Globe2 size={14} className="text-[#FF4500] shrink-0" /> <span>150+ countries</span></div>
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-2 text-center sm:text-left"><Headphones size={14} className="text-[#FF4500] shrink-0" /> <span>24/7 support</span></div>
            </div>
            {/* Mobile ring removed per user request — the scrolling cover
                strip at the top of the hero stays, but no partial 3D ring
                below the trust pills. */}
          </div>
          <div className="lg:col-span-5 hidden lg:flex items-start justify-end -mt-2 xl:-mt-2 pr-0 lg:-mr-6 xl:-mr-10">
            <div style={{ transform: "translateY(-140px)" }}>
              <AlbumCoverRing size={420} />
            </div>
          </div>
        </div>
      </section>

      {/* MOTTO STRIP */}
      <section className="landing-strip">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF4500] font-display"><Heart size={12} /> Our motto</div>
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl mt-5 tracking-tight leading-tight">
            <span className="landing-fg">We're the first distributor focused on <span className="gradient-text-accent">AI music</span> — because we believe</span><br />
            <span className="landing-fg">every song deserves to be heard.</span>
          </h2>
          <p className="landing-muted mt-6 max-w-2xl mx-auto leading-relaxed">
            Traditional distributors reject AI music. We embrace it. DistributeMusic.AI is built by AI artists, for AI artists — bringing your generated tracks to a global audience while keeping you fully compliant with every DSP's content policy.
          </p>
        </div>
      </section>

      {/* AI SOURCES SUPPORTED */}
      <section id="sources" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF4500] font-display"><Bot size={12} /> Any AI music engine</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight">Distribute music from <span className="gradient-text-accent">every AI generator.</span></h2>
            <p className="landing-muted mt-6 leading-relaxed">
              Whether you made it with <strong className="landing-fg">Suno AI</strong>, <strong className="landing-fg">Udio</strong>, <strong className="landing-fg">Meta MusicGen</strong>, our built-in AI Studio, or any other text-to-music engine — we accept it. Drop your audio file, tag the source, hit distribute. We handle the rest.
            </p>
            <div className="mt-6 dm-accent-glow inline-flex items-center gap-2 bg-[#FF4500] text-black font-semibold rounded-full px-5 py-2 text-sm">
              <Zap size={14} /> Distribute all kinds of AI-generated music — only on DistributeMusic.AI
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {AI_GENERATORS.map((g, i) => (
              <div key={g} className="landing-chip flex items-center gap-2" style={{ animationDelay: `${i * 70}ms` }} data-testid={`landing-ai-source-${i}`}>
                <span className="w-2 h-2 rounded-full bg-[#FF4500]" />
                <span className="font-display tracking-tight">{g}</span>
              </div>
            ))}
            <div className="landing-chip landing-chip-accent flex items-center gap-2 col-span-2 sm:col-span-3">
              <Sparkles size={14} /> + any other AI music generator
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS STRIP */}
      <section className="landing-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 flex flex-wrap items-center justify-center sm:justify-between gap-6 sm:gap-8">
          <span className="text-xs uppercase tracking-[0.25em] landing-muted font-display">Delivering AI music to</span>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 sm:gap-x-10 sm:gap-y-4">
            {PLATFORMS.map((p) => (
              <span key={p} className="font-display landing-fg text-base sm:text-lg tracking-tight">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* AI STUDIO */}
      <section id="ai-studio" className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF4500] font-display"><Wand2 size={12} /> AI Studio</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight">Don't have a track? Generate one.</h2>
            <p className="landing-muted mt-5 leading-relaxed">
              No AI tools? No problem. Our built-in AI Studio composes a complete track from a single text prompt — ready to distribute. Or upload your Suno / Udio export directly.
            </p>
            <ul className="mt-8 space-y-3 text-sm landing-fg">
              <li className="flex items-start gap-2"><Bot size={16} className="text-[#FF4500] mt-0.5" /> Text-to-song generation in seconds</li>
              <li className="flex items-start gap-2"><Radio size={16} className="text-[#FF4500] mt-0.5" /> One-click distribute to Spotify, Apple, TikTok…</li>
              <li className="flex items-start gap-2"><ShieldCheck size={16} className="text-[#FF4500] mt-0.5" /> Auto AI-disclosure for platform compliance</li>
              <li className="flex items-start gap-2"><BarChart3 size={16} className="text-[#FF4500] mt-0.5" /> Track streams & royalties in real-time</li>
            </ul>
            <Button onClick={launch} className="mt-8 bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full h-11 px-6 dm-accent-glow" data-testid="ai-studio-cta">
              Try AI Studio <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
          <div className="landing-card p-5 sm:p-7 relative overflow-hidden">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4500] font-display">Prompt</div>
            <div className="font-display text-xl sm:text-2xl mt-2 landing-fg">"Dreamy synthwave with arpeggios and a driving bassline at 110bpm"</div>
            <div className="mt-6 h-32 rounded-lg bg-gradient-to-r from-[#FF4500]/40 via-[#FF4500]/10 to-transparent flex items-end gap-1 p-4 overflow-hidden">
              {Array.from({ length: 48 }).map((_, i) => (
                <div key={i} className="w-1.5 bg-[#FF4500] rounded-full landing-bar" style={{ height: `${20 + Math.abs(Math.sin(i / 2)) * 80}%`, opacity: 0.6 + Math.abs(Math.sin(i / 3)) * 0.4, animationDelay: `${i * 30}ms` }} />
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3 text-sm landing-muted">
              <div className="w-9 h-9 rounded-full bg-[#FF4500] flex items-center justify-center text-black"><Music2 size={14} /></div>
              <div>
                <div className="landing-fg">Generated track — 30s preview</div>
                <div className="text-xs">Auto-tagged: AI-generated · Synthwave</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="landing-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-28">
          <div className="mb-10 sm:mb-16 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-display">Features</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight">An AI-native label, in your browser.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
            <FeatureCard className="md:col-span-7 md:row-span-2" icon={<Wand2 />} title="AI Music Generation"
              desc="">
              <div className="mt-6 relative rounded-lg overflow-hidden bg-gradient-to-br from-[#FF4500]/20 via-[#FF4500]/5 to-transparent border border-[#FF4500]/25 p-6 sm:p-8 lg:p-10" data-testid="features-ai-music-card">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF4500] font-display mb-4">Bring · Generate · Distribute</div>
                <p className="font-display text-xl sm:text-2xl lg:text-3xl leading-tight tracking-tight landing-fg">
                  Describe a song, get a finished track. <span className="landing-muted">Or bring your</span>{" "}
                  <span className="gradient-text-accent">Suno / Udio / MusicGen</span>{" "}
                  <span className="landing-muted">exports — we distribute all of them.</span>
                </p>
              </div>
            </FeatureCard>
            <FeatureCard className="md:col-span-5" icon={<Bot />} title="Distro AI — chat your song into Spotify">
              <div className="mt-3 rounded-lg border border-[#FF4500]/25 bg-[#FF4500]/[0.04] px-3.5 py-3 text-[12px] leading-relaxed landing-fg" data-testid="features-distro-ai-card">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4500] font-display mb-1.5">Speak · Chat · Release</div>
                <span className="landing-muted">Forget the traditional release form.</span> Just say
                <span className="text-[#FF4500] font-semibold"> "release a new song called Ocean Dreams"</span>
                — Distro AI generates it, picks a cover, auto-fills every field, and ships to <span className="text-white">150+ DSPs</span> in one short chat.
              </div>
            </FeatureCard>
            <FeatureCard className="md:col-span-5" icon={<Globe2 />} title="Global DSP Delivery" desc="One submission. Every streaming platform on earth. DDEX-compliant pipeline reaching 150+ stores." />
            <FeatureCard className="md:col-span-4" icon={<ShieldCheck />} title="AI-Disclosure Compliance" desc="Spotify, Apple & TikTok require AI tagging — we handle it automatically for every release." />
            <FeatureCard className="md:col-span-4" icon={<BarChart3 />} title="Live Analytics" desc="Streams, revenue, top tracks and platform splits — refreshed in real-time across all DSPs." />
            <FeatureCard className="md:col-span-4" icon={<Sparkles />} title="100% Royalties" desc="Keep every dollar. We never take a percentage cut of your streaming revenue." />
            <FeatureCard className="md:col-span-12" icon={<Headphones />} title="Smart Pre-Saves" desc="Generate fan-link campaigns that route listeners straight to their favourite DSP — automatically." />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="mb-10 sm:mb-16 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-display">Transparent pricing</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight">Simple plans. No commission. Ever.</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
            {TIERS.map((t) => (
              <div key={t.name} data-testid={`pricing-tier-${t.name.toLowerCase()}`}
                className={`landing-card p-6 sm:p-8 flex flex-col relative ${t.featured ? "border-[#FF4500]/50 dm-accent-glow sm:col-span-2 md:col-span-1" : ""}`}>
                {t.featured && (
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#FF4500] font-display mb-3">Most popular</span>
                )}
                {!t.featured && <span className="block h-[18px]" />}
                {/* Floating corner "50% OFF · LIMITED TIME" pill — beats with
                    a subtle heartbeat pulse and adapts to light/dark themes
                    via the `.promo-corner-pill` class in index.css. */}
                {t.discountBadge && (
                  <span
                    className="promo-corner-pill absolute -top-3 right-4 sm:right-6 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-widest font-display whitespace-nowrap"
                    data-testid={`pricing-discount-${t.name.toLowerCase()}`}
                  >
                    {t.discountBadge} · Limited time
                  </span>
                )}
                <h3 className="font-display text-xl sm:text-2xl tracking-tight">{t.name}</h3>
                <p className="text-sm landing-muted mt-1">{t.desc}</p>
                <div className="mt-5 sm:mt-6 flex items-end gap-2">
                  {t.originalPrice && (
                    <span className="font-display text-2xl sm:text-3xl tracking-tight landing-muted line-through pb-1" data-testid={`pricing-original-${t.name.toLowerCase()}`}>
                      {t.originalPrice}
                    </span>
                  )}
                  <span className="font-display text-4xl sm:text-5xl tracking-tight">{t.price}</span>
                  <span className="landing-muted text-sm pb-2">{t.period}</span>
                </div>
                <ul className="mt-8 space-y-3 text-sm landing-fg flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-[#FF4500] mt-1 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => goPlan(t.planId)} className={`mt-8 w-full rounded-full ${t.featured ? "bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold" : "landing-btn-outline"}`}
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}>
                  {t.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — mirrors JSON-LD, targets "People Also Ask" carousel in Google */}
      <section id="faq" className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-28" data-testid="faq-section" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-[#FF4500] font-display mb-3">Music distribution FAQ</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            <span className="gradient-text-accent">Music distribution</span> — everything artists ask.
          </h2>
          <p className="landing-muted mt-4 text-sm sm:text-base">The most-searched questions about AI music distribution, Suno &amp; Udio releases, DSP delivery and royalties — answered.</p>
        </div>
        <div className="space-y-3">
          {[
            {
              q: "What is AI music distribution and how does it work?",
              a: "AI music distribution is the process of delivering AI-generated songs — created with tools like Suno, Udio, MusicGen or Riffusion — to streaming services such as Spotify, Apple Music, TikTok, YouTube Music, Amazon Music and TIDAL. DistributeMusic.AI ingests your tracks, attaches the required AI-disclosure metadata (DDEX ERN 4.3), passes QC, and ships them to 150+ DSPs worldwide within 2–7 business days.",
            },
            {
              q: "Can I distribute Suno or Udio music to Spotify?",
              a: "Yes. Both Spotify and Apple Music accept AI-generated music that is properly disclosed and does not impersonate a real artist. DistributeMusic.AI is the first distributor built specifically for AI artists — every Suno, Udio, MusicGen, Riffusion or custom AI track you upload is auto-tagged as AI-generated so it stays compliant on every major DSP.",
            },
            {
              q: "How much does music distribution cost for AI artists?",
              a: "Starter is free forever — distribute 1 release at no cost. Pro is a flat $20/year for unlimited releases + AI Studio access — same price on first invoice and every renewal. Label is a flat $50/year — always, no discounts, no first-year pricing tricks. Every plan keeps 100% of your royalties — we never take a cut of streams or ownership.",
            },
            {
              q: "How long does it take for my music to appear on Spotify and Apple Music?",
              a: "Once your release passes our QC review (usually within 24 hours), Spotify and Apple Music typically go live in 2–4 business days. TikTok, YouTube Music, TIDAL, Amazon Music and Deezer follow within 5–7 days. Total time from upload → global streaming: about one week.",
            },
            {
              q: "Do I own the copyright to my AI-generated music?",
              a: "Yes. You retain 100% of the copyright and 100% of the royalties. DistributeMusic.AI is a distributor only — we deliver your files to DSPs and collect your royalties. We never claim ownership, publishing rights, or a percentage of your streams.",
            },
            {
              q: "Is DistributeMusic.AI better than DistroKid, TuneCore or CD Baby for AI music?",
              a: "For AI-generated music, yes. DistributeMusic.AI is the only major distributor built exclusively for AI artists — we handle the AI-disclosure metadata automatically, don't reject your release for being AI-made, and have a QC team trained on AI-music-specific compliance (impersonation checks, prompt-provenance, ISRC assignment). Generic distributors will accept AI music but leave you to figure out disclosure yourself, which can trigger takedowns.",
            },
            {
              q: "Can I release music under multiple artist names?",
              a: "On Starter and Pro plans, each account is locked to one artist name to protect against impersonation and duplicate profiles. Upgrade to the Label plan ($50/year flat) to distribute unlimited artists under a single dashboard — perfect for AI labels, aggregators, and multi-project producers.",
            },
            {
              q: "What audio and artwork formats do you accept?",
              a: "Audio: uncompressed .WAV only (16 or 24-bit, 44.1 kHz+). Artwork: JPG or PNG, minimum 3000×3000 px, square. These are the industry-standard DSP requirements — releases outside these specs are auto-rejected during QC so you never get pulled off Spotify after going live.",
            },
          ].map((item, i) => (
            <details key={i} className="dm-card group" itemScope itemType="https://schema.org/Question" itemProp="mainEntity" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer p-5 sm:p-6 flex items-center justify-between gap-4 list-none">
                <h3 className="font-display text-base sm:text-lg landing-fg pr-2" itemProp="name">{item.q}</h3>
                <span className="text-[#FF4500] shrink-0 group-open:rotate-45 transition-transform text-xl leading-none" aria-hidden="true">+</span>
              </summary>
              <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-2 landing-muted text-sm leading-relaxed" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <span itemProp="text">{item.a}</span>
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-28">
        <div className="landing-card p-7 sm:p-12 lg:p-20 flex flex-col lg:flex-row gap-6 lg:gap-10 items-start lg:items-center justify-between landing-cta">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight">Ready to release your first AI track?</h2>
            <p className="landing-muted mt-3 sm:mt-4 text-sm sm:text-base">Bring your AI music — or generate one. Either way, you're live on every DSP in under 5 minutes.</p>
          </div>
          <Button onClick={launch} className="bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full h-11 sm:h-12 px-6 sm:px-8 dm-accent-glow w-full sm:w-auto" data-testid="cta-bottom-launch">
            Launch Dashboard <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </section>

      <SeoFooter />
    </div>
  );
}

function FeatureCard({ icon, title, desc, children, className = "" }) {
  return (
    <div className={`landing-card p-5 sm:p-7 hover:-translate-y-1 transition-transform duration-300 ${className}`}>
      <div className="w-9 h-9 rounded-md bg-[#FF4500]/10 text-[#FF4500] flex items-center justify-center mb-4 sm:mb-5">
        {React.cloneElement(icon, { size: 18 })}
      </div>
      <h3 className="font-display text-lg sm:text-xl tracking-tight">{title}</h3>
      {desc && <p className="text-sm landing-muted mt-2 leading-relaxed">{desc}</p>}
      {children}
    </div>
  );
}
